import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { Bar } from 'react-chartjs-2';
import { Table, Alert, Button, Input, FormGroup } from 'reactstrap';

import {
    fetchExternalApiInsights, INSIGHTS_DEFAULTS, durationBucketLabel, DURATION_BUCKETS,
} from '../helpers/externalApiInsights';
import { authErrorLabel } from '../helpers/externalApiStats';
import { statusBadgeClass } from '../helpers/externalApiLogs';
import { formatNumber, formatPercent, formatMs, formatBytes } from '../helpers/format';
import { formatPoints, formatLoadPercent, loadBadgeClass, estimateCostPoints } from '../helpers/externalApiCost';
import ExternalApiLogDetail from './externalApiLogDetail';

/**
* "Advanced analysis" (VD-725 follow-up): duplicate/burst detection, a
* send-time heatmap, slow requests and top errors, all on top of the same
* period/client/route/method filters the parent Analytics view already
* resolved. Every row that represents a group of requests links out to the
* logs view via `onOpenLogs`; a single request opens in place via the shared
* log detail modal.
*/

const palette = {
    surface: '#FFFFFF',
    border: '#E6EDF5',
    textPrimary: '#3A3F56',
    textSecondary: '#52514E',
    textMuted: '#818FA5',
    warning: '#fab219',
    critical: '#d03b3b',
    blue: '#2a78d6',
};

const styles = {
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 },
    title: { fontSize: 16, fontWeight: 700, color: palette.textPrimary, margin: 0 },
    settingsCard: {
        backgroundColor: palette.surface, borderRadius: 6, padding: '14px 16px', marginBottom: 16,
        boxShadow: '0 10px 20px rgba(0,0,0,0.06)', display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end',
    },
    filterGroup: { marginBottom: 0, minWidth: 120 },
    filterLabel: { fontSize: 11, fontWeight: 600, color: palette.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 },
    kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 12, marginBottom: 16 },
    tile: { backgroundColor: palette.surface, borderRadius: 6, padding: '14px 16px', boxShadow: '0 10px 20px rgba(0,0,0,0.06)' },
    tileLabel: { fontSize: 11, fontWeight: 600, color: palette.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 },
    tileValue: { fontSize: 22, fontWeight: 600, color: palette.textPrimary, lineHeight: 1.1 },
    sectionNote: { fontSize: 12, color: palette.textMuted, marginBottom: 12 },
    card: { backgroundColor: palette.surface, borderRadius: 6, padding: '16px 18px', boxShadow: '0 10px 20px rgba(0,0,0,0.06)', marginBottom: 16 },
    sectionTitle: { fontSize: 13, fontWeight: 700, color: palette.textPrimary, marginBottom: 10 },
    chip: { display: 'inline-flex', alignItems: 'center', margin: '2px 4px 2px 0', padding: '2px 8px', borderRadius: 10, backgroundColor: palette.border, color: palette.textPrimary, fontSize: 12 },
    mono: { fontFamily: "'SFMono-Regular', Menlo, Monaco, Consolas, monospace", fontSize: 12 },
    warningRow: { backgroundColor: '#FFF4E0' },
    link: { background: 'transparent', border: 'none', padding: 0, color: '#33405E', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 },
    heatmapGrid: { display: 'grid', gridTemplateColumns: '50px repeat(24, 1fr)', gap: 2 },
    heatmapHourLabel: { fontSize: 10, color: palette.textMuted, textAlign: 'center' },
    heatmapDayLabel: { fontSize: 12, color: palette.textSecondary, display: 'flex', alignItems: 'center' },
    heatmapCell: { aspectRatio: '1', borderRadius: 2, minHeight: 16 },
    emptyState: { textAlign: 'center', padding: '30px 20px', color: palette.textMuted, fontSize: 13 },
};

function Chips({ values, empty = '-' }) {
    if (!values || !values.length) {
        return <span style={{ color: palette.textMuted }}>{empty}</span>;
    }

    return <>{values.map((v, idx) => <span key={idx} style={styles.chip}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>)}</>;
}

function QueryChips({ query }) {
    if (!query || typeof query !== 'object' || !Object.keys(query).length) {
        return <span style={{ color: palette.textMuted }}>-</span>;
    }

    return <>{Object.keys(query).map((k) => <span key={k} style={styles.chip}>{k}={String(query[k])}</span>)}</>;
}

function Truncated({ value, lang }) {
    let [open, setOpen] = useState(false);
    let text = typeof value === 'string' ? value : JSON.stringify(value);

    if (!text) {
        return <span style={{ color: palette.textMuted }}>-</span>;
    }

    if (text.length <= 60) {
        return <span>{text}</span>;
    }

    return (
        <span>
            {open ? text : `${text.slice(0, 60)}…`}
            {' '}
            <button style={styles.link} onClick={() => setOpen((v) => !v)}>
                {open ? 'Show less'.translate(lang) : 'Show more'.translate(lang)}
            </button>
        </span>
    );
}

/**
* A row's client, from whichever shape that list carries (`client` as an
* object or a plain name, `clientId` alongside). Clickable when the parent
* can open a client and the row says which one.
*/
function ClientCell({ row, client, onOpenClient }) {
    let c = client !== undefined ? client : row.client;
    let name = c ? (typeof c === 'object' ? c.company || c.username || c.name : c) : null;
    let id = row.clientId || (c && typeof c === 'object' ? c._id || c.id || c.clientId : null);

    if (!name) {
        return <span style={{ color: palette.textMuted }}>{row.ip ? row.ip : '-'}</span>;
    }

    if (!onOpenClient || !id) {
        return <span>{name}</span>;
    }

    return (
        <button
            type="button"
            style={styles.link}
            onClick={(e) => {
                e.stopPropagation();
                onOpenClient({ clientId: id, name, username: c && typeof c === 'object' ? c.username : name, company: c && typeof c === 'object' ? c.company : undefined, customerNumber: c && typeof c === 'object' ? c.customerNumber : undefined });
            }}
        >
            {name}
        </button>
    );
}

const TABS = [
    { key: 'duplicates', label: 'Duplicates' },
    { key: 'bursts', label: 'Bursts' },
    { key: 'heatmap', label: 'Heatmap' },
    { key: 'slowest', label: 'Slowest' },
    { key: 'params', label: 'Params' },
    { key: 'topErrors', label: 'Top errors' },
    { key: 'db', label: 'Database' },
];

const DB_RETENTION_DAYS = 14;
const DB_TRAFFIC_HINT = 'Estimate: number of documents × average document size in the collection';

const DB_COLUMNS = [
    { key: 'collection', label: 'Collection', text: true },
    { key: 'op', label: 'Op', text: true },
    { key: 'queries', label: 'Queries' },
    { key: 'totalMs', label: 'Total ms' },
    { key: 'avgMs', label: 'Avg ms' },
    { key: 'maxMs', label: 'Max ms' },
    { key: 'docs', label: 'Docs' },
    { key: 'estimatedBytes', label: 'Traffic (est.)', title: DB_TRAFFIC_HINT },
    { key: 'docBytes', label: 'Avg doc size', title: 'Average document size in the collection (MongoDB collection stats: total size / number of documents)' },
    { key: 'errors', label: 'Errors' },
];

/**
* Collections × operations, sortable on the client (the rows are all here
* already). The Total row comes from `db.totals`, never from adding up the
* rows - `byCollection` is capped at 30 rows, the totals cover everything.
*/
function DbCollectionsTable({ byCollection, totals, lang }) {
    let [sort, setSort] = useState({ key: null, dir: -1 });
    let t = totals || {};

    let sorted = useMemo(() => {
        let rows = byCollection || [];
        if (!sort.key) return rows;
        let col = DB_COLUMNS.find((c) => c.key === sort.key);
        return rows.slice().sort((a, b) => {
            let va = a[sort.key];
            let vb = b[sort.key];
            // unknown values (null doc size / traffic) always go last
            let na = va === null || va === undefined;
            let nb = vb === null || vb === undefined;
            if (na || nb) return na === nb ? 0 : na ? 1 : -1;
            let cmp = col && col.text ? String(va).localeCompare(String(vb)) : Number(va) - Number(vb);
            return cmp * sort.dir;
        });
    }, [byCollection, sort]);

    let onSort = (key) => setSort((prev) => (prev.key === key ? { key, dir: -prev.dir } : { key, dir: DB_COLUMNS.find((c) => c.key === key).text ? 1 : -1 }));

    return (
        <Table responsive hover>
            <thead>
                <tr>
                    {DB_COLUMNS.map((c) => (
                        <th key={c.key} style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} title={c.title ? c.title.translate(lang) : undefined} onClick={() => onSort(c.key)}>
                            {c.label.translate(lang)} {sort.key === c.key ? (sort.dir === 1 ? '▲' : '▼') : ''}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {!sorted.length ? (
                    <tr><td colSpan={DB_COLUMNS.length} style={styles.emptyState}>{'No requests in the selected period'.translate(lang)}</td></tr>
                ) : sorted.map((row, idx) => (
                    <tr key={idx}>
                        <td style={styles.mono}>{row.collection}</td>
                        <td style={styles.mono}>{row.op}</td>
                        <td>{formatNumber(row.queries)}</td>
                        <td>{formatMs(row.totalMs)}</td>
                        <td>{formatMs(row.avgMs)}</td>
                        <td>{formatMs(row.maxMs)}</td>
                        <td>{formatNumber(row.docs)}</td>
                        <td>{formatBytes(row.estimatedBytes)}</td>
                        <td>{formatBytes(row.docBytes)}</td>
                        <td>{formatNumber(row.errors)}</td>
                    </tr>
                ))}
            </tbody>
            {sorted.length ? (
                <tfoot>
                    <tr style={{ fontWeight: 600, borderTop: `2px solid ${palette.border}` }}>
                        <td colSpan={2}>{'Total'.translate(lang)}</td>
                        <td>{formatNumber(t.queries)}</td>
                        <td>{formatMs(t.totalMs)}</td>
                        <td>{formatMs(t.avgMsPerQuery)}</td>
                        <td>—</td>
                        <td>{formatNumber(t.docs)}</td>
                        <td>{formatBytes(t.estimatedBytes)}</td>
                        <td>—</td>
                        <td>{formatNumber(t.errors)}</td>
                    </tr>
                </tfoot>
            ) : null}
        </Table>
    );
}

function ExternalApiInsights({ lang, clientId, apiKeyId, route, method, from, to, totalsRequests, onOpenLogs, onOpenClient }) {
    // the Client column only says something when several clients are mixed together
    let showClient = !clientId;
    let [settings, setSettings] = useState(INSIGHTS_DEFAULTS);
    let [settingsDraft, setSettingsDraft] = useState(INSIGHTS_DEFAULTS);
    let [showSettings, setShowSettings] = useState(false);
    let [data, setData] = useState(null);
    let [loading, setLoading] = useState(true);
    let [error, setError] = useState(null);
    let [activeTab, setActiveTab] = useState('duplicates');
    let [paramsTab, setParamsTab] = useState('queryParams');
    let [openLogId, setOpenLogId] = useState(null);

    let requestKey = useMemo(() => JSON.stringify({ from, to, clientId, apiKeyId, route, method, settings }), [from, to, clientId, apiKeyId, route, method, settings]);

    useEffect(() => {
        if (!from || !to) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        fetchExternalApiInsights({ from, to, clientId, apiKeyId, route, method, ...settings }).then(({ data: result, error: err }) => {
            setLoading(false);
            if (err) {
                setError(err);
                setData(null);
            } else {
                setData(result);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    let openLogsWith = (patch) => onOpenLogs && onOpenLogs({ from, to, clientId, apiKeyId, route, method, ...patch });

    let hasStatusWarning = (statuses) => statuses && statuses.length > 1;

    return (
        <div>
            <div style={styles.header}>
                <h3 style={styles.title}>{'Advanced analysis'.translate(lang)}</h3>
                <Button size="sm" color="secondary" outline onClick={() => { setSettingsDraft(settings); setShowSettings((v) => !v); }}>
                    {'Analysis settings'.translate(lang)}
                </Button>
            </div>

            {showSettings ? (
                <div style={styles.settingsCard}>
                    <FormGroup style={styles.filterGroup}>
                        <div style={styles.filterLabel}>{'Duplicate window (s)'.translate(lang)}</div>
                        <Input type="number" min={1} max={3600} style={{ height: 38 }} value={settingsDraft.duplicateWindowSec}
                            onChange={(e) => setSettingsDraft({ ...settingsDraft, duplicateWindowSec: e.target.value })} />
                    </FormGroup>
                    <FormGroup style={styles.filterGroup}>
                        <div style={styles.filterLabel}>{'Burst (req/min)'.translate(lang)}</div>
                        <Input type="number" min={1} style={{ height: 38 }} value={settingsDraft.burstPerMinute}
                            onChange={(e) => setSettingsDraft({ ...settingsDraft, burstPerMinute: e.target.value })} />
                    </FormGroup>
                    <FormGroup style={styles.filterGroup}>
                        <div style={styles.filterLabel}>{'Slow requests shown'.translate(lang)}</div>
                        <Input type="number" min={1} max={50} style={{ height: 38 }} value={settingsDraft.slowLimit}
                            onChange={(e) => setSettingsDraft({ ...settingsDraft, slowLimit: e.target.value })} />
                    </FormGroup>
                    <FormGroup style={styles.filterGroup}>
                        <div style={styles.filterLabel}>{'Slow threshold (ms)'.translate(lang)}</div>
                        <Input type="number" min={1} style={{ height: 38 }} value={settingsDraft.slowThresholdMs}
                            onChange={(e) => setSettingsDraft({ ...settingsDraft, slowThresholdMs: e.target.value })} />
                    </FormGroup>
                    <FormGroup style={styles.filterGroup}>
                        <div style={styles.filterLabel}>{'Timezone'.translate(lang)}</div>
                        <Input type="text" style={{ height: 38 }} value={settingsDraft.tz}
                            onChange={(e) => setSettingsDraft({ ...settingsDraft, tz: e.target.value })} />
                    </FormGroup>
                    <Button size="sm" color="primary" onClick={() => { setSettings(settingsDraft); setShowSettings(false); }}>
                        {'Apply'.translate(lang)}
                    </Button>
                    <Button size="sm" color="secondary" outline onClick={() => setSettingsDraft(INSIGHTS_DEFAULTS)}>
                        {'Reset to defaults'.translate(lang)}
                    </Button>
                </div>
            ) : null}

            {error ? <Alert color="danger">{error}</Alert> : null}

            {loading ? (
                <div style={styles.emptyState}>{'Loading...'.translate(lang)}</div>
            ) : data ? (
                <>
                    <div className="table-tabs">
                        {TABS.map((t) => (
                            <button key={t.key} className={activeTab === t.key ? 'active' : ''} onClick={() => setActiveTab(t.key)}>
                                {t.label.translate(lang)}
                            </button>
                        ))}
                    </div>

                    <div style={{ marginTop: 10 }}>
                        {activeTab === 'duplicates' ? (
                            <div>
                                <div style={styles.kpiRow}>
                                    <div style={styles.tile}>
                                        <div style={styles.tileLabel}>{'Duplicate requests'.translate(lang)}</div>
                                        <div style={styles.tileValue}>{formatNumber(data.duplicates.duplicateRequests)}</div>
                                    </div>
                                    <div style={styles.tile}>
                                        <div style={styles.tileLabel}>{'% of total'.translate(lang)}</div>
                                        <div style={styles.tileValue}>{totalsRequests !== undefined && totalsRequests !== null ? formatPercent(data.duplicates.duplicateRequests, totalsRequests) : '–'}</div>
                                    </div>
                                    <div style={styles.tile}>
                                        <div style={styles.tileLabel}>{'Max in one window'.translate(lang)}</div>
                                        <div style={styles.tileValue}>{formatNumber(data.duplicates.maxInWindow)}</div>
                                    </div>
                                </div>

                                {!data.duplicates.duplicateRequests ? (
                                    <Alert color="info">
                                        {'No duplicates in this period. Duplicate detection only covers data collected after this feature was rolled out, so an older period may simply predate it.'.translate(lang)}
                                    </Alert>
                                ) : null}

                                <div style={styles.card}>
                                    <div style={styles.sectionTitle}>{'Most repeated requests'.translate(lang)}</div>
                                    <Table responsive hover>
                                        <thead>
                                            <tr>
                                                <th>{'Method'.translate(lang)}</th>
                                                <th>{'Route'.translate(lang)}</th>
                                                <th>{'Query'.translate(lang)}</th>
                                                <th>{'Client'.translate(lang)}</th>
                                                <th>{'Repeated'.translate(lang)}</th>
                                                <th>{'Statuses'.translate(lang)}</th>
                                                <th>{'First'.translate(lang)}</th>
                                                <th>{'Last'.translate(lang)}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!(data.duplicates.top || []).length ? (
                                                <tr><td colSpan={8} style={styles.emptyState}>{'No requests in the selected period'.translate(lang)}</td></tr>
                                            ) : data.duplicates.top.map((row, idx) => (
                                                <tr
                                                    key={idx}
                                                    style={{ cursor: 'pointer', ...(hasStatusWarning(row.statuses) ? styles.warningRow : {}) }}
                                                    onClick={() => openLogsWith({ fingerprint: row.fingerprint })}
                                                >
                                                    <td>{row.request ? row.request.method : '-'}</td>
                                                    <td style={styles.mono}>{row.request ? (row.request.route || row.request.path) : '-'}</td>
                                                    <td><QueryChips query={row.request ? row.request.query : null} /></td>
                                                    <td><ClientCell row={row.request || {}} onOpenClient={onOpenClient} /></td>
                                                    <td>{formatNumber(row.duplicateRequests)}</td>
                                                    <td>
                                                        <Chips values={row.statuses} />
                                                        {hasStatusWarning(row.statuses) ? (
                                                            <div style={{ fontSize: 11, color: palette.warning, marginTop: 2 }}>
                                                                {'Same POST sent more than once'.translate(lang)}
                                                            </div>
                                                        ) : null}
                                                    </td>
                                                    <td>{row.firstAt ? moment(row.firstAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                                                    <td>{row.lastAt ? moment(row.lastAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                    <div style={styles.sectionNote}>{'Bodies are not stored - only their hash, so an identical request is shown as "same body".'.translate(lang)}</div>
                                </div>

                                <div style={styles.card}>
                                    <div style={styles.sectionTitle}>{'Same X-Request-ID sent more than once'.translate(lang)}</div>
                                    <Table responsive hover>
                                        <thead>
                                            <tr>
                                                <th>{'Request ID'.translate(lang)}</th>
                                                <th>{'Method'.translate(lang)}</th>
                                                <th>{'Path'.translate(lang)}</th>
                                                <th>{'Client'.translate(lang)}</th>
                                                <th>{'Requests'.translate(lang)}</th>
                                                <th>{'Statuses'.translate(lang)}</th>
                                                <th>{'First'.translate(lang)}</th>
                                                <th>{'Last'.translate(lang)}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!(data.duplicates.repeatedRequestIds || []).length ? (
                                                <tr><td colSpan={8} style={styles.emptyState}>{'No requests in the selected period'.translate(lang)}</td></tr>
                                            ) : data.duplicates.repeatedRequestIds.map((row, idx) => (
                                                <tr key={idx} style={{ cursor: 'pointer' }} onClick={() => openLogsWith({ requestId: row.requestId })}>
                                                    <td style={styles.mono}>{row.requestId}</td>
                                                    <td>{row.method}</td>
                                                    <td style={styles.mono}>{row.path}</td>
                                                    <td><ClientCell row={row} onOpenClient={onOpenClient} /></td>
                                                    <td>{formatNumber(row.requests)}</td>
                                                    <td><Chips values={row.statuses} /></td>
                                                    <td>{row.firstAt ? moment(row.firstAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                                                    <td>{row.lastAt ? moment(row.lastAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        ) : null}

                        {activeTab === 'bursts' ? (
                            <div>
                                <div style={styles.kpiRow}>
                                    <div style={styles.tile}>
                                        <div style={styles.tileLabel}>{'Burst minutes'.translate(lang)}</div>
                                        <div style={styles.tileValue}>{formatNumber(data.bursts.burstMinutes)}</div>
                                    </div>
                                    <div style={styles.tile}>
                                        <div style={styles.tileLabel}>{'Requests in bursts'.translate(lang)}</div>
                                        <div style={styles.tileValue}>{formatNumber(data.bursts.requestsInBursts)}</div>
                                    </div>
                                    <div style={styles.tile}>
                                        <div style={styles.tileLabel}>{'Max per minute'.translate(lang)}</div>
                                        <div style={styles.tileValue}>{formatNumber(data.bursts.maxPerMinute)}</div>
                                    </div>
                                </div>

                                <div style={styles.card}>
                                    <div style={styles.sectionTitle}>{`${'Minutes at or above'.translate(lang)} ${(data.settings && data.settings.burstPerMinute) || settings.burstPerMinute} req/min`}</div>
                                    <Table responsive hover>
                                        <thead>
                                            <tr>
                                                <th>{'Minute'.translate(lang)}</th>
                                                <th>{'Load'.translate(lang)}</th>
                                                <th>{'Client'.translate(lang)}</th>
                                                <th>{'Points'.translate(lang)}</th>
                                                <th>{'Requests'.translate(lang)}</th>
                                                <th>{'Errors'.translate(lang)}</th>
                                                <th>{'Routes'.translate(lang)}</th>
                                                <th>{'IPs'.translate(lang)}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!(data.bursts.top || []).length ? (
                                                <tr><td colSpan={8} style={styles.emptyState}>{'No requests in the selected period'.translate(lang)}</td></tr>
                                            ) : data.bursts.top.map((row, idx) => (
                                                <tr
                                                    key={idx}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => openLogsWith({
                                                        from: row.minute,
                                                        to: moment(row.minute).add(60, 'seconds').toISOString(),
                                                        clientId: row.clientId,
                                                    })}
                                                >
                                                    <td>{row.minute ? moment(row.minute).format('YYYY-MM-DD HH:mm') : '-'}</td>
                                                    <td><span className={loadBadgeClass(row.loadPercent, data.cost)}>{formatLoadPercent(row.loadPercent)}</span></td>
                                                    <td><ClientCell row={row} onOpenClient={onOpenClient} /></td>
                                                    <td>{formatPoints(row.points)}</td>
                                                    <td>{formatNumber(row.requests)}</td>
                                                    <td>{formatNumber(row.errors)}</td>
                                                    <td><Chips values={row.routes} /></td>
                                                    <td><Chips values={row.ips} /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        ) : null}

                        {activeTab === 'heatmap' ? (
                            <div style={styles.card}>
                                <div style={styles.sectionTitle}>{'When clients send requests'.translate(lang)}</div>
                                <Heatmap heatmap={data.heatmap} lang={lang} />
                                <div style={styles.sectionNote}>{`${'Timezone'.translate(lang)}: ${(data.range && data.range.tz) || settings.tz}`}</div>
                            </div>
                        ) : null}

                        {activeTab === 'slowest' ? (
                            <div>
                                <div style={styles.card}>
                                    <div style={styles.sectionTitle}>{`${'Slowest requests'.translate(lang)} (> ${formatMs((data.settings && data.settings.slowThresholdMs) || settings.slowThresholdMs)})`}</div>
                                    <Table responsive hover>
                                        <thead>
                                            <tr>
                                                <th>{'Time'.translate(lang)}</th>
                                                <th>{'Client'.translate(lang)}</th>
                                                <th>{'Method / Path'.translate(lang)}</th>
                                                <th>{'Params'.translate(lang)}</th>
                                                <th>{'Status'.translate(lang)}</th>
                                                <th>{'Duration'.translate(lang)}</th>
                                                <th>{'Points'.translate(lang)}</th>
                                                <th>{'IP'.translate(lang)}</th>
                                                <th>{'User agent'.translate(lang)}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!(data.slowest || []).length ? (
                                                <tr><td colSpan={9} style={styles.emptyState}>{'No requests in the selected period'.translate(lang)}</td></tr>
                                            ) : data.slowest.map((row) => {
                                                let threshold = (data.settings && data.settings.slowThresholdMs) || settings.slowThresholdMs;
                                                let points = row.costPoints !== undefined && row.costPoints !== null
                                                    ? row.costPoints
                                                    : estimateCostPoints(row.durationMs, data.cost && data.cost.pointMs);
                                                return (
                                                    <tr key={row._id} style={{ cursor: 'pointer' }} onClick={() => setOpenLogId(row._id)}>
                                                        <td>{row.createdAt ? moment(row.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}</td>
                                                        <td><ClientCell row={row} onOpenClient={onOpenClient} /></td>
                                                        <td style={styles.mono}>{row.method} {row.path || row.route}</td>
                                                        <td><QueryChips query={{ ...(row.params || {}), ...(row.query || {}) }} /></td>
                                                        <td><span className={statusBadgeClass(row.status)}>{row.status}</span></td>
                                                        <td style={{ color: Number(row.durationMs) > Number(threshold) ? palette.critical : palette.textPrimary, fontWeight: 600 }}>
                                                            {formatMs(row.durationMs)}
                                                        </td>
                                                        <td>{points !== null ? formatPoints(points) : '-'}</td>
                                                        <td style={styles.mono}>{row.ip || '-'}</td>
                                                        <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.userAgent}>{row.userAgent || '-'}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>

                                <div style={styles.card}>
                                    <div style={styles.sectionTitle}>{'Slow routes'.translate(lang)}</div>
                                    <Table responsive hover>
                                        <thead>
                                            <tr>
                                                <th>{'Method'.translate(lang)}</th>
                                                <th>{'Route'.translate(lang)}</th>
                                                <th>{'Points'.translate(lang)}</th>
                                                <th>{'Busy seconds'.translate(lang)}</th>
                                                <th>{'Requests'.translate(lang)}</th>
                                                <th>{'Avg duration'.translate(lang)}</th>
                                                <th>{'Max duration'.translate(lang)}</th>
                                                <th>{'Slow requests'.translate(lang)}</th>
                                                <th>{'Slow %'.translate(lang)}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!(data.slowRoutes || []).length ? (
                                                <tr><td colSpan={9} style={styles.emptyState}>{'No requests in the selected period'.translate(lang)}</td></tr>
                                            ) : data.slowRoutes.map((row, idx) => (
                                                <tr key={idx}>
                                                    <td>{row.method}</td>
                                                    <td style={styles.mono}>{row.route}</td>
                                                    <td>{formatPoints(row.points)}</td>
                                                    <td>{formatNumber(row.busySeconds)}</td>
                                                    <td>{formatNumber(row.requests)}</td>
                                                    <td>{formatMs(row.avgDurationMs)}</td>
                                                    <td>{formatMs(row.maxDurationMs)}</td>
                                                    <td>{formatNumber(row.slowRequests)}</td>
                                                    <td>{Number(row.slowPercent || 0).toFixed(1)}%</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>

                                <div style={styles.card}>
                                    <div style={styles.sectionTitle}>{'Duration histogram'.translate(lang)}</div>
                                    <DurationHistogram histogram={data.durationHistogram} lang={lang} />
                                </div>
                            </div>
                        ) : null}

                        {activeTab === 'params' ? (
                            <div style={styles.card}>
                                <div className="table-tabs" style={{ marginBottom: 14 }}>
                                    <button className={paramsTab === 'queryParams' ? 'active' : ''} onClick={() => setParamsTab('queryParams')}>{'Query params'.translate(lang)}</button>
                                    <button className={paramsTab === 'pathParams' ? 'active' : ''} onClick={() => setParamsTab('pathParams')}>{'Path params'.translate(lang)}</button>
                                </div>
                                <Table responsive hover>
                                    <thead>
                                        <tr>
                                            <th>{'Parameter'.translate(lang)}</th>
                                            <th>{'Requests'.translate(lang)}</th>
                                            <th>{'Distinct values'.translate(lang)}</th>
                                            <th>{'Top values'.translate(lang)}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!(data[paramsTab] || []).length ? (
                                            <tr><td colSpan={4} style={styles.emptyState}>{'No requests in the selected period'.translate(lang)}</td></tr>
                                        ) : data[paramsTab].map((row, idx) => (
                                            <tr key={idx}>
                                                <td style={styles.mono}>{row.key}</td>
                                                <td>{formatNumber(row.requests)}</td>
                                                <td>{formatNumber(row.distinctValues)}</td>
                                                <td>
                                                    {(row.topValues || []).map((v, i) => (
                                                        <span key={i} style={styles.chip}>{String(v.value)} ×{v.requests}</span>
                                                    ))}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        ) : null}

                        {activeTab === 'topErrors' ? (
                            <div style={styles.card}>
                                <Table responsive hover>
                                    <thead>
                                        <tr>
                                            <th>{'Method / Route'.translate(lang)}</th>
                                            <th>{'Status'.translate(lang)}</th>
                                            <th>{'Reason'.translate(lang)}</th>
                                            <th>{'Requests'.translate(lang)}</th>
                                            <th>{'Clients'.translate(lang)}</th>
                                            <th>{'Last'.translate(lang)}</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!(data.topErrors || []).length ? (
                                            <tr><td colSpan={7} style={styles.emptyState}>{'No requests in the selected period'.translate(lang)}</td></tr>
                                        ) : data.topErrors.map((row, idx) => {
                                            let firstClient = (row.clients || [])[0];
                                            let clientIdFromRow = firstClient && typeof firstClient === 'object' ? (firstClient.id || firstClient._id) : null;

                                            return (
                                                <tr
                                                    key={idx}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => openLogsWith({ route: row.route, method: row.method, status: row.status, ...(clientIdFromRow ? { clientId: clientIdFromRow } : {}) })}
                                                >
                                                    <td style={styles.mono}>{row.method} {row.route}</td>
                                                    <td><span className={statusBadgeClass(row.status)}>{row.status}</span></td>
                                                    <td>{row.code ? authErrorLabel(row.code) : <Truncated value={row.error} lang={lang} />}</td>
                                                    <td>{formatNumber(row.requests)}</td>
                                                    <td><Chips values={(row.clients || []).map((c) => (typeof c === 'object' ? c.username : c))} /></td>
                                                    <td>{row.lastAt ? moment(row.lastAt).format('YYYY-MM-DD HH:mm') : '-'}</td>
                                                    <td>
                                                        {row.sampleLogId ? (
                                                            <Button size="sm" color="secondary" outline onClick={(e) => { e.stopPropagation(); setOpenLogId(row.sampleLogId); }}>
                                                                {'Example'.translate(lang)}
                                                            </Button>
                                                        ) : null}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        ) : null}

                        {activeTab === 'db' ? (
                            <div>
                                {moment().diff(moment(from), 'days', true) > DB_RETENTION_DAYS ? (
                                    <Alert color="info">
                                        {`${'Query details are only kept for'.translate(lang)} ${DB_RETENTION_DAYS} ${'days - some of this period may be missing them.'.translate(lang)}`}
                                    </Alert>
                                ) : null}

                                <div style={styles.kpiRow}>
                                    <div style={styles.tile}>
                                        <div style={styles.tileLabel}>{'DB queries'.translate(lang)}</div>
                                        <div style={styles.tileValue}>{formatNumber(data.db.totals.queries)}</div>
                                    </div>
                                    <div style={styles.tile}>
                                        <div style={styles.tileLabel}>{'Avg per request'.translate(lang)}</div>
                                        <div style={styles.tileValue}>{Number(data.db.totals.avgQueriesPerRequest || 0).toFixed(1)}</div>
                                    </div>
                                    <div style={styles.tile}>
                                        <div style={styles.tileLabel}>{'Avg ms per query'.translate(lang)}</div>
                                        <div style={styles.tileValue}>{formatMs(data.db.totals.avgMsPerQuery)}</div>
                                    </div>
                                    <div style={styles.tile}>
                                        <div style={styles.tileLabel}>{'Errors'.translate(lang)}</div>
                                        <div style={styles.tileValue}>{formatNumber(data.db.totals.errors)}</div>
                                    </div>
                                    <div style={styles.tile} title={DB_TRAFFIC_HINT.translate(lang)}>
                                        <div style={styles.tileLabel}>{'Total traffic (est.)'.translate(lang)}</div>
                                        <div style={styles.tileValue}>{formatBytes(data.db.totals.estimatedBytes)}</div>
                                    </div>
                                    <div style={styles.tile} title={DB_TRAFFIC_HINT.translate(lang)}>
                                        <div style={styles.tileLabel}>{'Avg per request (est.)'.translate(lang)}</div>
                                        <div style={styles.tileValue}>{formatBytes(data.db.totals.estimatedBytesPerRequest)}</div>
                                    </div>
                                </div>

                                <div style={styles.card}>
                                    <div style={styles.sectionTitle}>{'Collections and operations'.translate(lang)}</div>
                                    <div style={{ marginBottom: 16 }}>
                                        <DbCollectionsChart byCollection={data.db.byCollection} lang={lang} />
                                    </div>
                                    <DbCollectionsTable byCollection={data.db.byCollection} totals={data.db.totals} lang={lang} />
                                </div>

                                <div style={styles.card}>
                                    <div style={styles.sectionTitle}>{'Slowest queries'.translate(lang)}</div>
                                    <Table responsive hover>
                                        <thead>
                                            <tr>
                                                <th>{'Time'.translate(lang)}</th>
                                                {showClient ? <th>{'Client'.translate(lang)}</th> : null}
                                                <th>{'Route'.translate(lang)}</th>
                                                <th>{'Collection.op'.translate(lang)}</th>
                                                <th>{'Duration'.translate(lang)}</th>
                                                <th>{'Docs'.translate(lang)}</th>
                                                <th>{'Payload'.translate(lang)}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!(data.db.slowestQueries || []).length ? (
                                                <tr><td colSpan={showClient ? 7 : 6} style={styles.emptyState}>{'No requests in the selected period'.translate(lang)}</td></tr>
                                            ) : data.db.slowestQueries.map((row, idx) => (
                                                <tr key={idx} style={{ cursor: 'pointer' }} onClick={() => setOpenLogId(row.logId)}>
                                                    <td>{row.createdAt ? moment(row.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}</td>
                                                    {showClient ? <td><ClientCell row={row} onOpenClient={onOpenClient} /></td> : null}
                                                    <td style={styles.mono}>{row.method} {row.route}</td>
                                                    <td style={styles.mono}>{row.query ? `${row.query.collection}.${row.query.op}` : '-'}</td>
                                                    <td style={{ fontWeight: 600, color: row.query && Number(row.query.durationMs) > 100 ? palette.critical : palette.textPrimary }}>
                                                        {formatMs(row.query ? row.query.durationMs : null)}
                                                    </td>
                                                    <td>{row.query ? formatNumber(row.query.docs) : '-'}</td>
                                                    <td><Truncated value={row.query ? (row.query.filter || row.query.pipeline || row.query.update || row.query.payload) : null} lang={lang} /></td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>

                                <div style={styles.card}>
                                    <div style={styles.sectionTitle}>{'Same query repeated in one request'.translate(lang)}</div>
                                    <div style={styles.sectionNote}>{'These are candidates for optimization (N+1).'.translate(lang)}</div>
                                    <Table responsive hover>
                                        <thead>
                                            <tr>
                                                <th>{'Route'.translate(lang)}</th>
                                                <th>{'Collection.op'.translate(lang)}</th>
                                                <th>{'Filter'.translate(lang)}</th>
                                                <th>{'In requests'.translate(lang)}</th>
                                                <th>{'Max in one request'.translate(lang)}</th>
                                                <th>{'Extra queries'.translate(lang)}</th>
                                                <th>{'Total ms'.translate(lang)}</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!(data.db.repeatedInRequest || []).length ? (
                                                <tr><td colSpan={8} style={styles.emptyState}>{'No requests in the selected period'.translate(lang)}</td></tr>
                                            ) : data.db.repeatedInRequest.map((row, idx) => (
                                                <tr
                                                    key={idx}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => openLogsWith({ route: row.route })}
                                                >
                                                    <td style={styles.mono}>{row.route}</td>
                                                    <td style={styles.mono}>{row.collection}.{row.op}</td>
                                                    <td><Truncated value={row.filter} lang={lang} /></td>
                                                    <td>{formatNumber(row.requests)}</td>
                                                    <td>{formatNumber(row.maxTimesInRequest)}</td>
                                                    <td>{formatNumber(row.extraQueries)}</td>
                                                    <td>{formatMs(row.totalMs)}</td>
                                                    <td>
                                                        {row.sampleLogId ? (
                                                            <Button size="sm" color="secondary" outline onClick={(e) => { e.stopPropagation(); setOpenLogId(row.sampleLogId); }}>
                                                                {'Example'.translate(lang)}
                                                            </Button>
                                                        ) : null}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>

                                <div style={styles.card}>
                                    <div style={styles.sectionTitle}>{'Requests with the most queries'.translate(lang)}</div>
                                    <Table responsive hover>
                                        <thead>
                                            <tr>
                                                <th>{'Time'.translate(lang)}</th>
                                                <th>{'Client'.translate(lang)}</th>
                                                <th>{'Route'.translate(lang)}</th>
                                                <th>{'DB queries'.translate(lang)}</th>
                                                <th>{'DB ms'.translate(lang)}</th>
                                                <th>{'Duration'.translate(lang)}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {!(data.db.heaviestRequests || []).length ? (
                                                <tr><td colSpan={6} style={styles.emptyState}>{'No requests in the selected period'.translate(lang)}</td></tr>
                                            ) : data.db.heaviestRequests.map((row) => (
                                                <tr key={row._id} style={{ cursor: 'pointer' }} onClick={() => setOpenLogId(row._id)}>
                                                    <td>{row.createdAt ? moment(row.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-'}</td>
                                                    <td><ClientCell row={row} onOpenClient={onOpenClient} /></td>
                                                    <td style={styles.mono}>{row.method} {row.route}</td>
                                                    <td>{row.db ? formatNumber(row.db.queries) : '-'}</td>
                                                    <td>{row.db ? formatMs(row.db.totalMs) : '-'}</td>
                                                    <td>{formatMs(row.durationMs)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </>
            ) : null}

            <ExternalApiLogDetail
                lang={lang}
                logId={openLogId}
                onClose={() => setOpenLogId(null)}
                onOpenLogs={(patch) => { setOpenLogId(null); openLogsWith(patch); }}
            />
        </div>
    );
}

function Heatmap({ heatmap, lang }) {
    let max = 1;
    (heatmap || []).forEach((day) => (day.hours || []).forEach((h) => { if (h.requests > max) max = h.requests; }));

    return (
        <div style={styles.heatmapGrid}>
            <div />
            {Array.from({ length: 24 }).map((_, h) => <div key={h} style={styles.heatmapHourLabel}>{h}</div>)}

            {(heatmap || []).map((day) => (
                <React.Fragment key={day.day}>
                    <div style={styles.heatmapDayLabel}>{day.day}</div>
                    {(day.hours || []).map((cell) => {
                        let alpha = cell.requests ? Math.max(0.08, cell.requests / max) : 0;
                        return (
                            <div
                                key={cell.hour}
                                style={{
                                    ...styles.heatmapCell,
                                    backgroundColor: alpha ? `rgba(42, 120, 214, ${alpha})` : palette.border,
                                }}
                                title={`${day.day} ${cell.hour}:00 — ${cell.requests} ${'requests'.translate(lang)}, ${cell.errors} ${'errors'.translate(lang)}`}
                            />
                        );
                    })}
                </React.Fragment>
            ))}
        </div>
    );
}

function DurationHistogram({ histogram, lang }) {
    let byFrom = {};
    (histogram || []).forEach((row) => { byFrom[row.fromMs] = row.requests; });

    let labels = DURATION_BUCKETS.map((ms) => durationBucketLabel(ms));
    let values = DURATION_BUCKETS.map((ms) => byFrom[ms] || 0);

    let chartData = {
        labels,
        datasets: [{
            label: 'Requests'.translate(lang),
            data: values,
            backgroundColor: palette.blue,
            borderRadius: 4,
            maxBarThickness: 40,
        }],
    };

    let options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            x: { grid: { display: false }, ticks: { color: palette.textMuted, font: { size: 11 } } },
            y: { beginAtZero: true, grid: { color: palette.border }, ticks: { color: palette.textMuted, precision: 0, font: { size: 11 } } },
        },
    };

    return <div style={{ height: 220 }}><Bar data={chartData} options={options} /></div>;
}

/**
* "What's costing us in the DB" (VD-725): magnitude across collection.op
* pairs, by total time spent - the sequential default, one hue. Top 12 kept
* so labels stay legible; the table below carries the full top 30.
*/
function DbCollectionsChart({ byCollection, lang }) {
    let rows = (byCollection || []).slice(0, 12);

    if (!rows.length) {
        return <div style={styles.emptyState}>{'No requests in the selected period'.translate(lang)}</div>;
    }

    let chartData = {
        labels: rows.map((r) => `${r.collection}.${r.op}`),
        datasets: [{
            label: 'Total ms'.translate(lang),
            data: rows.map((r) => r.totalMs),
            backgroundColor: palette.blue,
            borderRadius: 4,
            maxBarThickness: 36,
        }],
    };

    let options = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            x: { beginAtZero: true, grid: { color: palette.border }, ticks: { color: palette.textMuted, font: { size: 11 } } },
            y: { grid: { display: false }, ticks: { color: palette.textMuted, font: { size: 11 } } },
        },
    };

    return <div style={{ height: Math.max(180, rows.length * 32) }}><Bar data={chartData} options={options} /></div>;
}

export default ExternalApiInsights;
